/**
 * Mantenimiento Ventas Generales — migración web de ares9620.
 *
 * Lista las ventas generales por rango de fechas, categoría y nombre del
 * cliente (los tres filtros se aplican en el servidor) y permite cancelarlas
 * (borrado lógico: gps.valor = 0). Solo se pueden cancelar ventas sin pagos
 * aplicados, y "con pagos" son los recibos vivos de pagosgps (`tienepagos`),
 * no el acumulador gps.cobrado: una venta al contado nace con cobrado = valor
 * y sin recibo, así que con esa regla nunca se habría podido cancelar. El
 * backend lo re-verifica. Cancelar requiere el permiso `borrar` de ares9620.
 * También reimprime el comprobante de la venta y corrige el número de GPS
 * (permiso `modificar` de ares9620; ver editargpsdialog.js).
 */
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import {
  FP,
  PageHeader,
  fpInputSx,
  fpDataGridSx,
  RazonCancelacionField,
  TooltipRazonCancelacion,
} from "components/financepro";
import { useApi } from "lib/api";
import { imprimirReciboVentaGeneral } from "modules/ventasgenerales/registrarventageneral";
import EditarGpsDialog from "modules/ventasgenerales/editargpsdialog";
import { puedeAccionPrograma } from "lib/seguridad/permisosPorModulo";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const inicioMesISO = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin ventas generales con esos filtros
      </Typography>
    </Stack>
  );
}

export default function MantenimientoVentasGenerales({ embedded = false, refreshToken = 0 } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [{ roleDetalles }] = useMaterialUIController();
  // Corregir el número de GPS: flag `modificar` de ares9620 (el backend lo
  // re-valida en permisos.py).
  const puedeEditarGps = puedeAccionPrograma(roleDetalles, "ares9620", "modificar");
  const [editarGps, setEditarGps] = useState(null);

  const [fecha1, setFecha1] = useState(inicioMesISO());
  const [fecha2, setFecha2] = useState(hoyISO());
  const [categoria, setCategoria] = useState("");
  const [cliente, setCliente] = useState("");
  const [sucursal, setSucursal] = useState("");
  const [config, setConfig] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compania, setCompania] = useState(null);

  useEffect(() => {
    post("/api/ventageneral_config", {}).then(({ ok, data }) => {
      if (ok) {
        setConfig(data);
        setCompania(data.compania || null);
        setCategorias(data.categorias || []);
        // Sin alcance multi-sucursal (admin o permiso `versucursales`) el
        // usuario queda fijado a su sucursal; el backend lo vuelve a forzar.
        if (!data.versucursales && data.sucursal) setSucursal(String(data.sucursal));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/ventageneral_listar", {
        fechadesde: fecha1,
        fechahasta: fecha2,
        categoria,
        cliente: cliente.trim(),
        sucursal,
      });
      if (ok) setRows(data.ventas || []);
      else showAlert({ message: data.Error || "No se pudieron listar las ventas", color: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // ---- Cancelación ----
  const [target, setTarget] = useState(null);
  const [razon, setRazon] = useState("");
  // Cada factura que se abre para cancelar empieza sin razón.
  useEffect(() => setRazon(""), [target]);
  const [cancelando, setCancelando] = useState(false);

  const cancelar = async () => {
    setCancelando(true);
    try {
      const { ok, data } = await post("/api/ventageneral_cancelar", { id: target.id, razon });
      if (ok) {
        showAlert({ message: data.ok || "Venta cancelada", color: "success" });
        setTarget(null);
        cargar();
      } else {
        showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
      }
    } finally {
      setCancelando(false);
    }
  };

  const reimprimir = (row) => {
    imprimirReciboVentaGeneral({
      compania,
      fecha: row.fecha,
      tipogasto: row.tipogasto,
      tipofactura: row.tipofactura,
      noprest: row.noprest || "",
      cedula: row.cedula,
      nombres: row.nombres,
      gps: row.gps,
      valor: row.valor,
      tipodepago: row.banco,
      referenciapago: row.referencia,
      descripcion: row.descripcion,
    });
  };

  // Una venta distribuida en las cuotas se saca desde el préstamo, no de aquí.
  // Si el backend todavía no manda `tienepagos` (despliegue viejo) se cae a la
  // regla anterior sobre gps.cobrado, que es la conservadora.
  const noSePuedeCancelar = (r) =>
    !!r.cancelada ||
    !!r.distribuida ||
    (r.tienepagos === undefined ? parseFloat(r.cobrado || 0) !== 0 : !!r.tienepagos);

  const columns = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "tipogasto", headerName: "Categoría", width: 125 },
    { field: "tipofactura", headerName: "Tipo", width: 95 },
    { field: "nombres", headerName: "Cliente", flex: 1, minWidth: 170 },
    { field: "cedula", headerName: "Cédula", width: 135 },
    {
      field: "gps",
      headerName: "GPS #",
      width: 150,
      renderCell: (params) => (
        <Stack direction="row" alignItems="center" spacing={0.4} sx={{ height: "100%" }}>
          <span>{params.row.gps || "—"}</span>
          {puedeEditarGps && !params.row.cancelada && (
            <IconButton
              size="small"
              title="Cambiar número de GPS"
              onClick={() => setEditarGps(params.row)}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Stack>
      ),
    },
    {
      field: "valor",
      headerName: "Valor",
      width: 110,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
    {
      field: "cobrado",
      headerName: "Cobrado",
      width: 130,
      align: "right",
      headerAlign: "right",
      // Una venta distribuida no está "cobrada": su valor pasó a las cuotas
      // del préstamo (por eso guarda cobrado = valor). Lo que sí entró lo dice
      // el libro de asignación, cuota por cuota.
      renderCell: (params) =>
        params.row.distribuida
          ? `${fmtMoney(params.row.cobradoencuotas || 0)} en cuotas`
          : fmtMoney(params.row.cobrado),
    },
    {
      field: "cancelada",
      headerName: "Estado",
      width: 110,
      renderCell: (params) =>
        params.row.cancelada ? (
          <TooltipRazonCancelacion razon={params.row.razonanula}>
            <Chip
              size="small"
              label="Cancelada"
              sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 700 }}
            />
          </TooltipRazonCancelacion>
        ) : (
          <Chip
            size="small"
            label="Vigente"
            sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.8}>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setTarget(params.row)}
            disabled={noSePuedeCancelar(params.row)}
            sx={btnOutline(FP.color.danger)}
          >
            Cancelar
          </Button>
          <Button
            size="small"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => reimprimir(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Imprimir
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box
      component={embedded ? "section" : "main"}
      sx={embedded ? {} : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Mantenimiento Ventas Generales"
          subtitle="Cancela ventas sin pagos aplicados — la cancelación requiere permiso de borrado"
        />
      )}
      <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={fecha1}
                onChange={(e) => setFecha1(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={fecha2}
                onChange={(e) => setFecha2(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Categoría"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {categorias.map((c) => (
                  <MenuItem key={c.id} value={c.categoria}>
                    {c.categoria}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                fullWidth
                label="Cliente"
                placeholder="Nombre o parte"
                value={cliente}
                onChange={(e) => setCliente(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && cargar()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                {config?.versucursales !== false && <MenuItem value="">Todas</MenuItem>}
                {(config?.sucursales || [])
                  .filter((s) => config?.versucursales !== false || s.sucursal === config?.sucursal)
                  .map((s) => (
                    <MenuItem key={s.sucursal} value={s.sucursal}>
                      {s.sucursal}-{s.descripcion}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={cargar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}
            >
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} ventas
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <EditarGpsDialog
        venta={editarGps}
        onClose={() => setEditarGps(null)}
        onSaved={(gps) =>
          setRows((prev) => prev.map((r) => (r.id === editarGps?.id ? { ...r, gps } : r)))
        }
      />

      {/* Confirmación de cancelación */}
      <Dialog
        open={!!target}
        onClose={() => setTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: FP.color.danger }}>
          Cancelar venta general
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem" }}>
            Factura <b>#{target?.id}</b> de <b>{target?.nombres}</b> por RD${" "}
            <b>{fmtMoney(target?.valor)}</b> ({target?.tipogasto}). La venta quedará anulada (valor
            0)
            {target?.tipofactura === "Contado"
              ? ", y el cobro registrado con ella también se revierte"
              : ""}
            . Esta acción no se puede deshacer.
          </DialogContentText>
          <RazonCancelacionField value={razon} onChange={setRazon} disabled={cancelando} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTarget(null)} sx={btnGhost}>
            No
          </Button>
          <Button
            startIcon={
              cancelando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={cancelar}
            disabled={cancelando}
            sx={btnSolid("#D32F2F", "#B71C1C")}
          >
            Sí, cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
